import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import type { IPartnerDocumentSection } from '../../../lib/documentsAndLinks'
import { selectCockpitGeoMappingItems, selectCockpitPartnerData } from '../../../model/selectors'
import { countDocuments } from '../lib/countDocuments'
import { resolveDocumentGroups } from '../lib/resolveDocumentGroups'
import type {
  IDocumentGroup,
  IDocumentsNavigation,
  IDocumentsSummaryRow,
  TDocumentsGrouping,
  TDocumentType,
} from '../types'

const emptySelection = { selectedGroupId: null, selectedPartnerId: null, geoPath: [] }

export const useCockpitDocumentsGrouped = (
  sections: IPartnerDocumentSection[],
  readOnly: boolean,
  groupingEnabled = true,
  initialDocumentType?: TDocumentType
) => {
  const [navigation, setNavigation] = useState<IDocumentsNavigation>({
    ...emptySelection,
    grouping: groupingEnabled ? 'geo' : 'partner',
    documentType: initialDocumentType ?? null,
  })
  const { grouping, documentType, geoPath, selectedGroupId, selectedPartnerId } = navigation
  const isSinglePartner = sections.length === 1
  const partners = useSelector(selectCockpitPartnerData)
  const geoMapping = useSelector(selectCockpitGeoMappingItems)
  const resolved = useMemo(
    () =>
      isSinglePartner
        ? { groups: [], geoPath, isPartnerLevel: true }
        : resolveDocumentGroups({
            sections,
            grouping,
            documentType,
            geoPath,
            partners,
            geoMapping,
            readOnly,
          }),
    [sections, grouping, documentType, geoPath, partners, geoMapping, readOnly, isSinglePartner]
  )
  const { groups, isPartnerLevel } = resolved
  const selected = groups.find((group) => group.id === selectedGroupId)
  const selectedPartner = selected?.sections.find(
    (section) => section.partnerId === selectedPartnerId
  )
  const detail =
    isSinglePartner && documentType
      ? { id: sections[0].partnerId, label: sections[0].partnerName, sections }
      : selectedPartner && selected
        ? { ...selected, sections: [selectedPartner] }
        : isPartnerLevel
          ? selected
          : undefined

  const resetGroups = () => setNavigation((current) => ({ ...current, ...emptySelection }))

  const openType = (type: TDocumentType | null) =>
    setNavigation((current) => ({ ...current, ...emptySelection, documentType: type }))

  const changeGrouping = (next: TDocumentsGrouping) =>
    setNavigation((current) => ({ ...current, ...emptySelection, grouping: next }))

  const goToGeoLevel = (index: number) =>
    setNavigation((current) => ({
      ...current,
      selectedGroupId: null,
      selectedPartnerId: null,
      geoPath: current.geoPath.slice(
        0,
        current.geoPath.indexOf(current.geoPath.filter((step) => !step.automatic)[index]) + 1
      ),
    }))

  const selectPartner = (partnerId: string | null) =>
    setNavigation((current) => ({ ...current, selectedPartnerId: partnerId }))

  const selectGroup = (group: IDocumentGroup) =>
    setNavigation((current) => {
      const singlePartner = group.sections.length === 1 ? group.sections[0] : undefined
      const hasGeoChildren =
        current.grouping === 'geo' &&
        !singlePartner &&
        !isPartnerLevel &&
        geoMapping.some((node) => node.parentId === group.id)
      return {
        ...current,
        selectedPartnerId: !isPartnerLevel && singlePartner ? singlePartner.partnerId : null,
        selectedGroupId: hasGeoChildren ? null : group.id,
        geoPath: hasGeoChildren
          ? [...resolved.geoPath, { id: group.id, label: group.label }]
          : current.geoPath,
      }
    })

  const getRows = (): IDocumentsSummaryRow[] => {
    if (!documentType) {
      return (['elt', 'qbr'] as const).map((type) => ({
        id: type,
        label: `${type.toUpperCase()} documents`,
        count: countDocuments(sections, type),
        onClick: () => openType(type),
      }))
    }

    if (selected) {
      return selected.sections.map((section) => ({
        id: section.partnerId,
        label: section.partnerName,
        count: countDocuments([section]),
        meta: section.partnerId,
        onClick: () => selectPartner(section.partnerId),
      }))
    }
    return groups.map((group) => ({
      id: group.id,
      label: group.label,
      count: countDocuments(group.sections),
      meta: isPartnerLevel ? group.id : `${group.sections.length} partners`,
      onClick: () => selectGroup(group),
    }))
  }

  return {
    grouping: isSinglePartner ? ('partner' as const) : grouping,
    isSinglePartner,
    showGrouping: groupingEnabled && !isSinglePartner,
    documentType,
    geoPath: geoPath.filter((step) => !step.automatic),
    selected,
    selectedPartner,
    detail,
    openType,
    changeGrouping,
    goToGeoLevel,
    resetGroups,
    selectPartner,
    rows: getRows(),
    totalCount: countDocuments(sections, documentType),
  }
}
